import { useState } from "react"
import { Alert, Box, Button, MenuItem, Stack, TextField } from "@mui/material"
import type { GridColDef, GridValueFormatterParams } from "@mui/x-data-grid-pro"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import type { MediaWriteAccess } from "../../hooks/useMediaUploads"
import type { Media, MediaKind, MediaStatus } from "../../interface/media"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import { useGetMediaListQuery } from "../../services/publications/mediaApi"
import { FeatureErrors } from "../common/featureErrors"
import { MediaDeleteDialog } from "./mediaDeleteDialog"
import { SavedMediaThumbnail } from "./mediaThumbnail"

export interface MediaLibraryProps extends MediaWriteAccess {
  /** Selection mode never renders DELETE. Only ready resources can be chosen. */
  onSelect?: (media: Media) => void
  selectedIds?: readonly string[]
  listKey?: string
}
export function MediaLibrary({
  readOnly,
  canWrite,
  onSelect,
  selectedIds = [],
  listKey,
}: MediaLibraryProps) {
  const list = usePostBotListState({
    resource: "media",
    key: listKey ?? (onSelect ? "mediaPicker" : "media"),
  })
  const query = useGetMediaListQuery({
    ...list.listArgs,
    status: onSelect ? "ready" : (list.listArgs.status ?? "ready"),
  })
  // `data` can retain another page/filter even after this request fails.
  // Only current-argument data may expose selection or deletion actions.
  const page = query.currentData
  const [deleting, setDeleting] = useState<Media | null>(null)
  const writable = !readOnly && canWrite !== false
  const columns: GridColDef<Media>[] = [
    { field: "originalFilename", headerName: "Файл", minWidth: 180, flex: 1 },
    {
      field: "kind",
      headerName: "Тип",
      width: 80,
      valueFormatter: ({ value }: GridValueFormatterParams<MediaKind>) =>
        value === "photo" ? "Фото" : "Видео",
    },
    {
      field: "size",
      headerName: "Размер",
      width: 100,
      valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
        value < 1024
          ? `${value} Б`
          : value < 1024 * 1024
            ? `${Number((value / 1024).toFixed(1))} КБ`
            : `${Number((value / 1024 / 1024).toFixed(1))} МБ`,
    },
    {
      field: "status",
      headerName: "Состояние",
      width: 110,
      valueFormatter: ({ value }: GridValueFormatterParams<MediaStatus>) =>
        value === "ready" ? "Готово" : "Удаляется",
    },
    {
      field: "preview",
      headerName: "Предпросмотр",
      width: 200,
      renderCell: ({ row }: { row: Media }) => (
        <SavedMediaThumbnail media={row} />
      ),
    },
    ...(onSelect || writable
      ? [
          {
            field: "mediaAction",
            headerName: "Действие",
            width: 180,
            renderCell: ({ row }: { row: Media }) =>
              onSelect ? (
                <Button
                  aria-label={`Выбрать ${row.originalFilename}`}
                  disabled={
                    !writable ||
                    row.status !== "ready" ||
                    selectedIds.includes(row.id)
                  }
                  onClick={() => {
                    if (
                      writable &&
                      row.status === "ready" &&
                      !selectedIds.includes(row.id)
                    )
                      onSelect(row)
                  }}
                >
                  {selectedIds.includes(row.id) ? "Выбрано" : "Выбрать"}
                </Button>
              ) : (
                <Button
                  color="error"
                  aria-label={`Удалить из библиотеки ${row.originalFilename}`}
                  onClick={() => setDeleting(row)}
                >
                  Удалить
                </Button>
              ),
          },
        ]
      : []),
  ].map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <TextField
          size="small"
          select
          label="Тип медиа"
          value={list.filters.kind ?? ""}
          onChange={(event) =>
            list.setFilters({
              kind: (event.target.value as MediaKind) || undefined,
            })
          }
          sx={{ width: { xs: "100%", sm: 220 }, maxWidth: "100%" }}
        >
          <MenuItem value="">Фото и видео</MenuItem>
          <MenuItem value="photo">Фото</MenuItem>
          <MenuItem value="video">Видео</MenuItem>
        </TextField>
        {!onSelect && (
          <TextField
            size="small"
            select
            label="Состояние медиа"
            value={list.filters.status ?? "ready"}
            onChange={(event) =>
              list.setFilters({ status: event.target.value as MediaStatus })
            }
            sx={{ width: { xs: "100%", sm: 220 }, maxWidth: "100%" }}
          >
            <MenuItem value="ready">Готово</MenuItem>
            <MenuItem value="deleting">Удаляется</MenuItem>
          </TextField>
        )}
      </Stack>
      <FeatureErrors error={query.error} />
      {query.error != null && (
        <Button
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Повторить загрузку
        </Button>
      )}
      {!query.isFetching && !query.error && page?.data.length === 0 && (
        <Alert severity="info">Медиа нет</Alert>
      )}
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={query.data?.total ?? 0}
          loading={query.isFetching}
          autoHeight
          rowHeight={120}
          disableRowSelectionOnClick
        />
      </Box>
      {deleting && !onSelect && (
        <MediaDeleteDialog
          media={deleting}
          readOnly={readOnly}
          canWrite={canWrite}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null)
            void query.refetch()
          }}
        />
      )}
    </Stack>
  )
}
